import type { ReactNode } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import ArticleHero from '../../../../components/news/ArticleHero';
import PullQuote from '../../../../components/news/PullQuote';
import KeyPointsBox from '../../../../components/news/KeyPointsBox';
import CommentBox from '../../../../components/news/CommentBox';
import SourceCard from '../../../../components/news/SourceCard';
import RelatedCards from '../../../../components/news/RelatedCards';

/* ---- In-article inline parts (editorial layout; no section bands) ---- */
function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}
function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        Source: {media} (<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>)
      </figcaption>
    </figure>
  );
}
function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}
const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function GlobalButlerShortageSalarySurgePageEN() {
  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'News', href: '/en/news' },
    { label: 'Global Butler Shortage Drives Salaries Over $130,000' }
  ];

  const article = {
    category: 'Industry Trends & Market Analysis',
    date: 'October 15, 2024',
    title: 'Global Butler Shortage Drives Salaries Over $130,000',
    subtitle: 'High Value of Butler Services Shown by Global Talent Shortage',
    heroImage: '/images/generated/jba-fs-074-news_global-butler-shortage-salary-surge.webp',
    lead: 'A global butler shortage has driven head butler salaries to over $130,000 annually. The concentration of wealthy families in cities, increased property purchases during the pandemic, and visa processing delays are cited as main causes, revealing a market situation where demand for butler services far exceeds supply.',
    sections: [
      {
        heading: 'Three Main Causes of Butler Shortage',
        image: '/images/generated/jba-fs-075-news_global-butler-shortage-salary-surge-v3.webp', aspect: 'aspect-[3/4]',
        alt: 'Luxury mansions concentrated in cities such as London and New York',
        paragraphs: [
          'According to Robb Report, there are three main factors behind the current butler shortage. First, the wealthiest families are concentrated in cities like London and New York, causing demand in these areas to surge rapidly.',
          'Second, many wealthy individuals purchased additional properties during the pandemic, requiring more staff accordingly. Third, bureaucratic delays in visa and other documentation processes, particularly after Brexit, have made talent acquisition difficult.',
          'Workers with British accents are reportedly in high demand among Asian and Middle Eastern families newly arrived in the UK, or families wanting to send their children to British schools.'
        ]
      },
      {
        heading: 'Rapid Salary Increases',
        image: '/images/generated/jba-fs-076-news_global-butler-shortage-salary-surge-v3.webp', aspect: 'aspect-[3/4]',
        alt: 'Rising butler pay driven by growing scarcity value',
        paragraphs: [
          'As a result of the talent shortage, butler salaries have increased significantly. Head butlers can now earn over $130,000 annually, clearly demonstrating the expertise and scarcity value of butler services.',
          'Nanny salaries have also increased by approximately $26,000 since COVID-19, showing that talent acquisition has become difficult across household services.',
          'According to experts, current staff will not consider jobs unless provided with accommodation (ideally a two-bedroom detached cottage with a private garden) and the ability to bring family and pets.'
        ]
      },
      {
        heading: 'Diverse Skills Required of Modern Butlers',
        image: '/images/generated/jba-fs-077-news_global-butler-shortage-salary-surge-v3.webp', aspect: 'aspect-[3/4]',
        alt: 'A modern butler balancing household management, travel and mentoring',
        paragraphs: [
          'Both work and the workforce have changed significantly. Modern butlers are required not only to manage households but also to travel with families, function as trusted confidantes, and even serve as mentors.',
          'Developing personnel with such diverse skill sets takes time and cost, making it a difficult investment to reduce. Meanwhile, younger workers tend not to want the long hours the job demands, and cheaper workers lack the necessary training and niche skill sets.',
          "The British Butler Institute, the world's largest butler training institution, provides education ranging from traditional skills like properly presenting newspapers to modern service techniques, highlighting the growing importance of such specialized education."
        ]
      },
      {
        heading: 'UK Market Special Circumstances',
        image: '/images/generated/jba-fs-078-news_global-butler-shortage-salary-surge-v3.webp', aspect: 'aspect-[4/3]',
        alt: 'A British country estate purchased by a wealthy family',
        paragraphs: [
          'According to The Times, particularly severe conditions are reported in the UK. While many Eastern European workers left the UK after Brexit and COVID-19, many wealthy families have purchased country estates.',
          'Furthermore, rising rents are pushing out personnel who maintain estates from the area. As a result, employers are forced to purchase neighboring cottages to attract staff, impacting the real estate market.'
        ]
      }
    ],
    comment: 'The global butler shortage and salary increases are important market signals clearly showing that the expertise and scarcity value of the butler profession are internationally recognized. The diverse skills required of modern butlers—from household management to travel accompaniment and mentoring functions—establish their status as highly specialized professionals beyond mere domestic services. Developing next-generation butler talent through quality training programs while adapting to changing work attitudes among younger generations is essential for sustainable industry development. In Japan as well, we will actively work toward butler talent development and value enhancement based on these global trends, aiming to establish international-standard butler services.',
    source: [
      { label: 'Source', value: 'The Hustle', link: false },
      { label: 'Article URL', value: 'https://thehustle.co/news/where-have-all-the-butlers-gone', link: true },
      { label: 'Access Date', value: 'October 2025', link: false }
    ],
    related: [
      { to: '/en/news/china-butler-demand-surge', image: '/images/generated/jba-fs-en-010-news_global-butler-shortage-salary-s.webp', aspect: 'aspect-[3/4]', alt: 'Related article: Surge in Butler Demand Among Chinese Wealthy', title: 'Surge in Butler Demand Among Chinese Wealthy', desc: "Rapid growth of butler services in Asia's largest market" },
      { to: '/en/news/ai-hospitality-revolution', image: '/images/generated/jba-fs-en-011-news_global-butler-shortage-salary-s.webp', aspect: 'aspect-[3/4]', alt: 'Related article: AI Hospitality Revolution: 5 Transformations', title: 'AI Hospitality Revolution: 5 Transformations', desc: 'The future of butler services opened by AI' }
    ]
  };
  const S = article.sections;

  const sources = [
    { label: 'Source', value: 'The Hustle', link: false },
    { label: 'Original Article', value: 'https://thehustle.co/news/where-have-all-the-butlers-gone', link: true },
    { label: 'Salary Levels', value: 'https://www.butlerschool.com/home/what-does-a-butler-earn/', link: true },
    { label: 'Modern Butler Pay', value: 'https://www.forbesglobalproperties.com/storied/the-modern-butler-at-your-service', link: true },
    { label: 'Salary Guide 2026', value: 'https://www.lighthouse-careers.com/blog/butler-salary-2026-complete-guide-to-private-service-pay-rates/', link: true },
    { label: 'Access Date', value: 'July 2026', link: false }
  ];
  const toc = [
    { id: 's-causes', label: 'Three Main Causes of Butler Shortage' },
    { id: 's-salary-comp', label: 'Butler Salary Benchmarks' },
    { id: 's-surge', label: 'Rapid Salary Increases' },
    { id: 's-skills', label: 'Diverse Skills Required of Modern Butlers' },
    { id: 's-training', label: 'Rising Demand and the Training Field' },
    { id: 's-uk', label: 'UK Market Special Circumstances' }
  ];

  return (
    <Layout>
      <title>Global Butler Shortage Drives Salaries Over $130,000 | Japan Butler Association</title>
      <meta name="description" content="A global butler shortage has driven head butler salaries over $130,000 annually, as demand for butler services far exceeds supply amid wealth concentration and post-Brexit visa delays." />
      <link rel="canonical" href="https://butler-association.jp/en/news/global-butler-shortage-salary-surge" />
      <link rel="alternate" hrefLang="ja" href="https://butler-association.jp/news/global-butler-shortage-salary-surge" />
      <link rel="alternate" hrefLang="en" href="https://butler-association.jp/en/news/global-butler-shortage-salary-surge" />

      <ArticleHero eyebrow="News / Market" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="Article Highlights"
              points={[
                'A global butler shortage has driven head butler salaries over $130,000 a year',
                'Wealth concentration in cities, pandemic-era property buying and post-Brexit visa delays are cited as main causes',
                'Modern butlers handle household management plus travel, confidante and mentor roles, making training costly and hard to cut'
              ]}
            />

            <SectionHeading id="s-causes" en="Causes">{S[0].heading}</SectionHeading>
            <div className="space-y-6">{S[0].paragraphs.slice(0, 2).map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
            <SourceShot
              src="/images/news-sources/shortage-thehustle.jpg"
              alt="Screenshot of The Hustle article on the butler shortage and salary surge"
              media="The Hustle"
              url="https://thehustle.co/news/where-have-all-the-butlers-gone"
            />
            <div className="space-y-6">{S[0].paragraphs.slice(2).map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-salary-comp" en="Salary Benchmarks">Butler Salary Benchmarks</SectionHeading>
            <div className="space-y-6">
              <p className={P}>According to luxury staffing agency Morgan & Mallet, top-tier butlers earn <Stat>$120,000–$200,000</Stat> a year. As roles grow more complex and demand outstrips supply, terms have been pushed to levels that favor practitioners.</p>
              <p className={P}>Figures from the Netherlands-based training school The International Butler Academy put European pay at <Stat>€40,000–€75,000</Stat> net (some above €100,000), a US average of about $54,000, and $50,000–$100,000 in the Middle East (occasionally exceeding $200,000)—showing a wide range by region and experience.</p>
            </div>
            <SourceShot
              src="/images/news-sources/shortage-butlerschool.jpg"
              alt="Screenshot of The International Butler Academy page explaining what a butler earns"
              media="The International Butler Academy"
              url="https://www.butlerschool.com/home/what-does-a-butler-earn/"
            />

            <SectionHeading id="s-surge" en="Salary Surge">{S[1].heading}</SectionHeading>
            <div className="space-y-6">{S[1].paragraphs.slice(0, 2).map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="Head butlers can now earn over $130,000 a year." cite="Robb Report / The Hustle" />
            </div>
            <div className="space-y-6">{S[1].paragraphs.slice(2).map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-skills" en="Modern Skills">{S[2].heading}</SectionHeading>
            <div className="space-y-6">{S[2].paragraphs.slice(0, 2).map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
            <SourceShot
              src="/images/news-sources/shortage-wikipedia.jpg"
              alt="Screenshot of the English Wikipedia page on the history and role of the butler profession"
              media="Wikipedia (Butler)"
              url="https://en.wikipedia.org/wiki/Butler"
            />
            <div className="space-y-6">{S[2].paragraphs.slice(2).map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-training" en="Demand & Training">Rising Demand and the Training Field</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Behind the shortage lie the growing ranks of the newly wealthy (HNWIs) and the widening role of the modern butler (security, IT, events, multi-property management). The International Butler Academy reports that applications for white-glove service rose <Stat>35%</Stat> between 2016 and 2022.</p>
              <p className={P}>Membership of the Guild of Professional English Butlers is also said to have grown by about <Stat>60%</Stat>, yet supply still cannot keep pace with demand—hence the "global butler shortage." Training schools teach everything from traditional etiquette to modern service techniques, and their importance is rising further.</p>
            </div>

            <SectionHeading id="s-uk" en="UK Market">{S[3].heading}</SectionHeading>
            <div className="space-y-6">{S[3].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="Contents" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">Head Butler Salary</dt><dd className="text-[#333] mt-0.5">over $130,000</dd></div>
                  <div><dt className="text-[#8f7a56]">Top Tier</dt><dd className="text-[#333] mt-0.5">$120K–$200K</dd></div>
                  <div><dt className="text-[#8f7a56]">US Average</dt><dd className="text-[#333] mt-0.5">approx. $54K</dd></div>
                  <div><dt className="text-[#8f7a56]">Applications (2016-22)</dt><dd className="text-[#333] mt-0.5">+35%</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="Comment from Japan Butler Association" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="Source" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="Related Articles" items={article.related} readLabel="Read" backHref="/en/news" backLabel="Back to News List" />
    </Layout>
  );
}
